// ============================================
// ModalEditarExpediente.js
// ============================================
// Form de edicion. Cualquier usuario logueado puede editar campos basicos.
// Si el expediente esta cerrado y el usuario NO es direccion, los campos
// quedan deshabilitados con warning visible.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  API_BASE_CLI, FF, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CL, COLOR_BORDE,
  COLOR_PELIGRO, inputStyle, botonSecundario, botonPrimario,
  authHeaders, ShellModal, ToastFlotante, esRolDireccion,
} from './helpersExpedientes';
import { useUsuario } from '../../context/UsuarioContext';
import SelectorClienteExpediente from '../SelectorClienteExpediente';
import { TIPOS_UBICACION } from '../../utils/ubicacionExpediente';
import { guardarBorrador, leerBorrador, borrarBorrador, borradorDifiere, haceCuanto } from '../../utils/borradorFormulario';

// Construye un _detalle minimal para el SelectorClienteExpediente a partir
// de los campos `cliente_*_nombre` que ya devuelve GET /api/oportunidades/:id.
// El selector solo necesita id + un nombre para mostrar el chip; al desplegar
// los combos cargara la lista completa via API y matcheara por id.
function detalleDesdeExpediente(exp) {
  if (!exp) return null;
  const d = {};
  if (exp.cliente_grupo_id) {
    d.grupo = { id: exp.cliente_grupo_id, nombre: exp.cliente_grupo_nombre || '' };
  }
  if (exp.cliente_razon_social_id) {
    d.razon_social = {
      id: exp.cliente_razon_social_id,
      razon_social: exp.cliente_razon_social_nombre || '',
    };
  }
  if (exp.cliente_sucursal_id) {
    d.sucursal = {
      id: exp.cliente_sucursal_id,
      nombre_interno: exp.cliente_sucursal_nombre || '',
      ciudad: exp.cliente_sucursal_ciudad || null,
    };
  }
  if (exp.cliente_persona_id) {
    d.persona = {
      id: exp.cliente_persona_id,
      nombre_completo: exp.cliente_persona_nombre || '',
    };
  }
  return Object.keys(d).length > 0 ? d : null;
}

export default function ModalEditarExpediente({ expediente, onCerrar, onGuardado, esCelular }) {
  const { usuario } = useUsuario();
  const direccion = esRolDireccion(usuario);
  const cerrado = expediente?.tipo_cierre != null;
  const bloqueado = cerrado && !direccion;

  const [tiposLugar, setTiposLugar] = useState([]);
  const [necesidades, setNecesidades] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [form, setForm] = useState({
    titulo:                expediente?.titulo || '',
    tipo_lugar:            expediente?.tipo_lugar || '',
    necesidad:             expediente?.necesidad || '',
    descripcion_necesidad: expediente?.descripcion_necesidad || '',
    // EXP-UBICACION: para completar expedientes sin sucursal (ej. Sebastian Selvas).
    tipo_ubicacion:            expediente?.tipo_ubicacion || '',
    ubicacion_especificacion:  expediente?.ubicacion_especificacion || '',
  });

  // Cliente vinculado (forma esperada por SelectorClienteExpediente)
  const [cliente, setCliente] = useState(() => ({
    cliente_id: expediente?.cliente_id || null,
    cliente_grupo_id: expediente?.cliente_grupo_id || null,
    cliente_razon_social_id: expediente?.cliente_razon_social_id || null,
    cliente_sucursal_id: expediente?.cliente_sucursal_id || null,
    cliente_persona_id: expediente?.cliente_persona_id || null,
    _resumen: expediente?.cliente_jerarquia_corta || '',
    _detalle: detalleDesdeExpediente(expediente),
  }));

  useEffect(() => {
    let activo = true;
    Promise.all([
      fetch(`${API_BASE_CLI}/api/catalogos/tipos-lugar`).then(r => r.json()).catch(() => []),
      fetch(`${API_BASE_CLI}/api/catalogos/tipos-necesidad`).then(r => r.json()).catch(() => []),
    ]).then(([tl, tn]) => {
      if (!activo) return;
      setTiposLugar(Array.isArray(tl) ? tl : []);
      setNecesidades(Array.isArray(tn) ? tn : []);
    });
    return () => { activo = false; };
  }, []);

  // El listener de Escape se monta arriba, antes de que exista `cerrarConAviso`; se llama a
  // traves de un ref para no usar una const antes de su declaracion (ReferenceError).
  const cerrarConAvisoRef = useRef(() => {});
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !enviando) cerrarConAvisoRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enviando, onCerrar]);

  // MOVIL-GUARDAR: red de seguridad. Lo capturado se guarda en el navegador mientras se escribe;
  // si la ventana se cierra o el navegador se recarga, al volver se ofrece recuperarlo.
  const idBorrador = expediente?.id || null;
  const [borradorPendiente, setBorradorPendiente] = useState(null);

  useEffect(() => {
    const b = leerBorrador(usuario?.id, 'expediente', idBorrador);
    if (b && borradorDifiere(b, form)) setBorradorPendiente(b);
    // solo al abrir: si se recalculara con cada tecleo, el aviso reapareceria sin parar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idBorrador, usuario?.id]);

  const recuperarBorrador = () => {
    if (borradorPendiente?.datos) setForm(prev => ({ ...prev, ...borradorPendiente.datos }));
    setBorradorPendiente(null);
  };
  const descartarBorrador = () => {
    borrarBorrador(usuario?.id, 'expediente', idBorrador);
    setBorradorPendiente(null);
  };

  const setCampo = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const cambios = useMemo(() => {
    const diff = {};
    // EXP-TITULO: titulo editable. Si se borra (queda vacio) se manda null
    // para limpiar la columna en BD.
    if ((form.titulo || '') !== (expediente?.titulo || '')) {
      diff.titulo = form.titulo && form.titulo.trim() ? form.titulo.trim() : null;
    }
    if (form.tipo_lugar !== (expediente?.tipo_lugar || ''))                       diff.tipo_lugar = form.tipo_lugar || null;
    if (form.necesidad !== (expediente?.necesidad || ''))                         diff.necesidad = form.necesidad || null;
    if (form.descripcion_necesidad !== (expediente?.descripcion_necesidad || '')) diff.descripcion_necesidad = form.descripcion_necesidad || null;
    if ((cliente.cliente_grupo_id || null) !== (expediente?.cliente_grupo_id || null)) {
      diff.cliente_grupo_id = cliente.cliente_grupo_id || null;
    }
    if ((cliente.cliente_razon_social_id || null) !== (expediente?.cliente_razon_social_id || null)) {
      diff.cliente_razon_social_id = cliente.cliente_razon_social_id || null;
    }
    if ((cliente.cliente_sucursal_id || null) !== (expediente?.cliente_sucursal_id || null)) {
      diff.cliente_sucursal_id = cliente.cliente_sucursal_id || null;
    }
    if ((cliente.cliente_persona_id || null) !== (expediente?.cliente_persona_id || null)) {
      diff.cliente_persona_id = cliente.cliente_persona_id || null;
    }
    // EXP-UBICACION: solo tiene sentido sin sucursal; con sucursal se limpian a null.
    const conSucursal = !!cliente.cliente_sucursal_id;
    const tipoUbicNuevo = conSucursal ? null : (form.tipo_ubicacion || null);
    const especUbicNuevo = conSucursal ? null : ((form.ubicacion_especificacion || '').trim() || null);
    if (tipoUbicNuevo !== (expediente?.tipo_ubicacion || null)) diff.tipo_ubicacion = tipoUbicNuevo;
    if (especUbicNuevo !== (expediente?.ubicacion_especificacion || null)) diff.ubicacion_especificacion = especUbicNuevo;
    return diff;
  }, [form, cliente, expediente]);

  const hayCambios = Object.keys(cambios).length > 0;

  // MOVIL-GUARDAR: no descartar en silencio. Si hay cambios sin guardar se pregunta; el borrador
  // queda igualmente en el navegador, asi que aunque se cierre, lo capturado se puede recuperar.
  const cerrarConAviso = () => {
    if (hayCambios && !enviando) {
      const aviso = [
        'Tienes cambios sin guardar. Si cierras ahora no se aplicaran.',
        'Lo capturado queda guardado en este dispositivo y podras recuperarlo al volver a abrir.',
        '¿Cerrar de todos modos?',
      ].join(String.fromCharCode(10, 10));
      const seguir = window.confirm(aviso);
      if (!seguir) return;
    }
    onCerrar();
  };
  cerrarConAvisoRef.current = cerrarConAviso;

  // Guardar el borrador mientras se captura (con respiro, para no escribir en cada tecla).
  useEffect(() => {
    if (bloqueado) return;
    const t = setTimeout(() => {
      if (hayCambios) guardarBorrador(usuario?.id, 'expediente', idBorrador, form);
    }, 600);
    return () => clearTimeout(t);
  }, [form, hayCambios, bloqueado, usuario?.id, idBorrador]);
  const sucursalFaltante = !!cliente.cliente_grupo_id && !cliente.cliente_sucursal_id;

  const guardar = async () => {
    if (!hayCambios || enviando || bloqueado) return;
    if (sucursalFaltante) {
      setError('Si vinculas un cliente, la sucursal es obligatoria.');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/oportunidades/${expediente.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ...cambios, usuario_id: usuario?.id || null }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401 || r.status === 403) {
        setError('No tienes permisos para editar este expediente.');
        return;
      }
      if (!r.ok) throw new Error(data?.error || `Error ${r.status}`);
      borrarBorrador(usuario?.id, 'expediente', idBorrador); // guardado en el servidor: el borrador sobra
      setToast({ tipo: 'ok', msg: 'Cambios guardados.' });
      setTimeout(() => onGuardado(data), 800);
    } catch (e) {
      setError(e.message || 'No se pudo guardar.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <ShellModal onClose={enviando ? () => {} : cerrarConAviso} ancho={600} esCelular={esCelular}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${COLOR_BORDE}`,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: COLOR_OSCURO, fontFamily: FF }}>
              Editar expediente
            </h2>
            <div style={{ marginTop: 4, fontSize: 12, color: COLOR_GRIS_CL }}>
              {expediente.folio}
            </div>
          </div>
          <button
            onClick={cerrarConAviso}
            disabled={enviando}
            aria-label="Cerrar"
            style={{
              background: 'transparent', border: 'none',
              cursor: enviando ? 'not-allowed' : 'pointer',
              color: COLOR_GRIS_CL, fontSize: 22, fontFamily: FF,
              // tactil >= 44 (medido en 23x37 antes del arreglo)
              minWidth: 44, minHeight: 44, padding: 0, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >{'×'}</button>
        </div>

        {/* MOVIL-GUARDAR: `flex:1` + `minHeight:0` hacen de esto un scrollport REAL que se encoge al
            alto disponible. Sin ellos el contenido empujaba la barra de acciones fuera de la caja y,
            con el `overflow:hidden` del shell, el boton Guardar quedaba recortado. */}
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {/* MOVIL-GUARDAR: rescate de lo capturado en una sesion anterior que no llego a guardarse. */}
          {borradorPendiente && !bloqueado && (
            <div style={{
              background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af',
              padding: '12px 14px', borderRadius: 10, fontSize: 13, lineHeight: 1.5,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Tenias algo capturado sin guardar ({haceCuanto(borradorPendiente.guardadoEn)})
              </div>
              <div style={{ marginBottom: 10 }}>Se quedo en este dispositivo. ¿Lo recuperamos?</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" onClick={recuperarBorrador} style={{
                  background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 14px', fontSize: 13, fontWeight: 700, fontFamily: FF,
                  cursor: 'pointer', minHeight: 44,
                }}>Recuperar lo que estaba capturando</button>
                <button type="button" onClick={descartarBorrador} style={{
                  background: '#fff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 8,
                  padding: '10px 14px', fontSize: 13, fontWeight: 600, fontFamily: FF,
                  cursor: 'pointer', minHeight: 44,
                }}>Descartar</button>
              </div>
            </div>
          )}
          {bloqueado && (
            <div style={{
              background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e',
              padding: '10px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            }}>
              Este expediente esta cerrado. Solo direccion puede editarlo.
            </div>
          )}

          {/* EXP-TITULO: titulo editable, max 150. Para legacy queda vacio
              hasta que el usuario lo llene. */}
          <Campo etiqueta="Titulo del expediente">
            <input
              type="text"
              value={form.titulo}
              onChange={(e) => setCampo('titulo', e.target.value.slice(0, 150))}
              placeholder="Ej: Proyecto CCTV Gimnasio Brooklyn Condesa"
              maxLength={150}
              disabled={bloqueado || enviando}
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: COLOR_GRIS_CL, marginTop: 4 }}>
              {form.titulo.length}/150 caracteres
            </div>
          </Campo>

          <Campo etiqueta="Tipo de lugar">
            <select
              value={form.tipo_lugar}
              onChange={(e) => setCampo('tipo_lugar', e.target.value)}
              disabled={bloqueado || enviando}
              style={inputStyle}
            >
              <option value="">-- sin definir --</option>
              {tiposLugar.map(t => (
                <option key={t.codigo} value={t.codigo}>{t.icono} {t.nombre}</option>
              ))}
            </select>
          </Campo>

          <Campo etiqueta="Necesidad">
            <select
              value={form.necesidad}
              onChange={(e) => setCampo('necesidad', e.target.value)}
              disabled={bloqueado || enviando}
              style={inputStyle}
            >
              <option value="">-- sin definir --</option>
              {necesidades.map(n => (
                <option key={n.codigo} value={n.codigo}>{n.icono} {n.nombre}</option>
              ))}
            </select>
          </Campo>

          <Campo etiqueta="Descripcion de la necesidad">
            <textarea
              value={form.descripcion_necesidad}
              onChange={(e) => setCampo('descripcion_necesidad', e.target.value)}
              rows={3}
              disabled={bloqueado || enviando}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: FF }}
            />
          </Campo>

          <div style={{ borderTop: `1px solid ${COLOR_BORDE}`, paddingTop: 16 }}>
            <div style={{
              fontSize: 12, color: COLOR_GRIS, fontWeight: 700, fontFamily: FF,
              textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10,
            }}>Cliente vinculado</div>
            <SelectorClienteExpediente
              valor={cliente}
              onChange={setCliente}
              esCelular={esCelular}
            />
          </div>

          {/* EXP-UBICACION: si el cliente no tiene sucursal, permite capturar/
              completar donde se realiza el proyecto (ej. Sebastian Selvas). */}
          {!cliente.cliente_sucursal_id && (
            <div style={{ borderTop: `1px solid ${COLOR_BORDE}`, paddingTop: 16 }}>
              <div style={{
                fontSize: 12, color: COLOR_GRIS, fontWeight: 700, fontFamily: FF,
                textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4,
              }}>Sucursal o ubicacion</div>
              <div style={{ fontSize: 12, color: COLOR_GRIS_CL, marginBottom: 10, fontFamily: FF, lineHeight: 1.4 }}>
                Sin sucursal: indica donde se realizara el proyecto (opcional).
              </div>
              <div style={{ display: 'grid', gap: 12, gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr' }}>
                <Campo etiqueta="Tipo de ubicacion">
                  <select
                    value={form.tipo_ubicacion}
                    onChange={(e) => setCampo('tipo_ubicacion', e.target.value)}
                    disabled={bloqueado || enviando}
                    style={inputStyle}
                  >
                    <option value="">Selecciona...</option>
                    {TIPOS_UBICACION.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </Campo>
                <Campo etiqueta="Especificacion">
                  <input
                    type="text"
                    value={form.ubicacion_especificacion}
                    onChange={(e) => setCampo('ubicacion_especificacion', e.target.value.slice(0, 200))}
                    placeholder="ej. Condesa, Torre B, Depto 4B"
                    maxLength={200}
                    disabled={bloqueado || enviando}
                    style={inputStyle}
                  />
                </Campo>
              </div>
            </div>
          )}

          {error && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
              padding: '8px 12px', borderRadius: 8, fontSize: 12,
            }}>{error}</div>
          )}
        </div>

        <div style={{
          // safe-area-inset-bottom: en iPhone con home indicator los botones no
          // quedan pegados al borde (crecio el form con la seccion de ubicacion).
          padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          borderTop: `1px solid ${COLOR_BORDE}`, background: '#fafafa',
          display: 'flex', justifyContent: 'space-between', gap: 10, flexShrink: 0,
        }}>
          <button onClick={cerrarConAviso} disabled={enviando} style={botonSecundario}>Cancelar</button>
          <button
            onClick={guardar}
            disabled={!hayCambios || enviando || bloqueado || sucursalFaltante}
            style={{
              ...botonPrimario,
              opacity: (!hayCambios || enviando || bloqueado || sucursalFaltante) ? 0.5 : 1,
              cursor: (!hayCambios || enviando || bloqueado || sucursalFaltante) ? 'not-allowed' : 'pointer',
            }}
          >{enviando ? 'Guardando...' : 'Guardar cambios'}</button>
        </div>
      </ShellModal>
      {toast && <ToastFlotante tipo={toast.tipo}>{toast.msg}</ToastFlotante>}
    </>
  );
}

function Campo({ etiqueta, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12, color: COLOR_GRIS, fontWeight: 600, fontFamily: FF }}>{etiqueta}</span>
      {children}
    </label>
  );
}
